import Header from "@/components/Header";
import SiteFooter from "@/components/SiteFooter";
import PageHero from "@/components/PageHero";
import BookCTA from "@/components/BookCTA";
import { Link } from "react-router-dom";
import { LOGO_URL } from "@/lib/brand";
import { usePageMeta } from "@/lib/seo";
import { Users, MapPin, Handshake, ShieldCheck, Recycle, Heart, ArrowRight } from "lucide-react";

const values = [
  { icon: Users, title: "Family run", body: "Small local team you actually get to know — no call centre, no hard sell." },
  { icon: MapPin, title: "Based in Peterborough", body: "We live and work here. When you book, we're never far away." },
  { icon: ShieldCheck, title: "Environment Agency registered", body: "Fully registered waste carriers — everything is disposed of the right way. Licence No. CBDU662542." },
  { icon: Recycle, title: "Recycle first", body: "We recycle, donate and reuse wherever possible. Landfill is always the last resort." },
  { icon: Handshake, title: "Honest quotes", body: "One clear price, no surprise add-ons, no obligation if it's not right for you." },
  { icon: Heart, title: "Handled with care", body: "Probate and bereavement clearances treated with the respect they deserve." },
];

export default function About() {
  usePageMeta(
    "About Us | Family Run House Clearance Peterborough",
    "Waites Clearances is a small, family run clearance business based in Peterborough. Honest quotes, careful work and Environment Agency registered — Peterborough's trusted clearance team."
  );
  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="page-about">
      <Header />

      <PageHero
        eyebrow="About us"
        title="A family run team, proud to be based in Peterborough."
        description="Waites Clearances is a small, family run business based in Peterborough. We started out helping neighbours, friends and local landlords with the heavy lifting — and we've kept that same approach ever since. When you call us, you're not going through a call centre or a national chain: it's a real family on the other end, and it'll be the same family turning up on the day."
      >
        <Link
          to="/team"
          data-testid="about-meet-team-link"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#FF7F00] hover:bg-[#E67000] text-white px-6 py-3 font-bold shadow-lg shadow-orange-200 transition-colors"
        >
          Meet the family team <ArrowRight className="h-4 w-4" />
        </Link>
      </PageHero>

      <section className="py-20 sm:py-24 bg-white border-y border-slate-100" data-testid="about-values">
        <div className="max-w-6xl mx-auto px-6 sm:px-10">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#FF7F00]">Our values</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              Local, careful, straight-talking.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              We treat every home across Peterborough with the respect we'd want for our own, whether it's a single
              item collection or an entire estate to clear. Honest quotes, tidy work, nothing left
              behind.
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {values.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                data-testid={`about-value-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#FFB870] hover:shadow-lg hover:-translate-y-0.5 transition-all"
              >
                <div className="h-11 w-11 rounded-xl bg-[#FFF3E6] flex items-center justify-center mb-4">
                  <Icon className="h-5 w-5 text-[#FF7F00]" />
                </div>
                <h3 className="font-display text-lg font-semibold text-slate-900">{title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{body}</p>
                {title === "Family run" && (
                  <Link
                    to="/team"
                    data-testid="about-family-run-team-link"
                    className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-[#FF7F00] hover:text-[#B85800]"
                  >
                    Meet the team <ArrowRight className="h-4 w-4" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-24" data-testid="about-quote">
        <div className="max-w-4xl mx-auto px-6 sm:px-10">
          <div className="rounded-3xl border border-[#FFB870] bg-gradient-to-br from-[#FFF7EE] to-white p-8 sm:p-12">
            <div className="flex items-center gap-4">
              <img
                src={LOGO_URL}
                alt="Waites Clearances logo"
                className="h-16 w-16 rounded-2xl object-contain bg-white border border-slate-200"
              />
              <div>
                <p className="font-display font-extrabold text-slate-900 leading-tight">
                  Waites <span className="text-[#FF7F00]">Clearances</span>
                </p>
                <p className="text-sm text-slate-500">Family run since day one</p>
              </div>
            </div>
            <blockquote className="mt-6 font-display text-xl sm:text-2xl italic text-slate-800 leading-snug">
              "We treat every clearance like we're doing it for family — because most of the time,
              we're doing it for someone in the neighbourhood."
            </blockquote>
          </div>
        </div>
      </section>

      <BookCTA
        title="Ready when you are."
        body="Send us the details and photos and Peterborough's friendly clearance team will take care of the rest."
      />

      <SiteFooter />
    </div>
  );
}
